import { SerializedConstructor } from '@langchain/core/load/serializable';
import { Modal, ModalClose, ModalDialog } from '@mui/joy';

import { useState } from 'react';
import { Link } from 'react-router-dom';

import { RepoForm, useRepoState } from '@/components/Hub/RepoForm';
import useToast from '@/components/Toast/useToast';
import { usePromptNameDuplicateCheck } from '@/hooks/usePromptNameDuplicateCheck';
import {
  useCreateCommitMutation,
  useCreateRepoMutation,
  useSelectedTenant,
} from '@/hooks/useSwr';
import { useCreateTagsOnResource } from '@/hooks/useTagsOnResource';
import {
  CommitWithLookupsSchema,
  RepoWithLookupsSchema,
  ResourceType,
} from '@/types/schema';
import { getRepoHandleValidationError } from '@/utils/hub';

import { appPromptsIndexPath, hubApiCommitsPath } from '../../utils/constants';
import { useFetchWorkspacePromptWebhook } from './components/Webhooks/hooks/useFetchWorkspacePromptWebhook';

export const PromptNewForm = ({
  manifest,
  onClose,
  onSuccess,
}: {
  manifest?: SerializedConstructor;
  onClose: () => void;
  onSuccess?: (
    repoWithLookups: RepoWithLookupsSchema,
    commit: CommitWithLookupsSchema
  ) => void;
}) => {
  const toast = useToast();
  const [repo, setRepo] = useRepoState();
  const { data } = useSelectedTenant();
  const tenantHandle = data?.tenant_handle ?? undefined;

  const {
    hasDuplicate: hasPromptWithSameName,
    isValidating: isValidatingPrompts,
  } = usePromptNameDuplicateCheck(repo.repo_handle);

  const { trigger: createCommit, isMutating: isCommitMutating } =
    useCreateCommitMutation(tenantHandle ?? '', repo.repo_handle ?? '', {
      onError: (error) => {
        toast.createToast({
          title: 'Failed to create commit',
          description: error.message,
        });
      },
    });

  const {
    trigger: createTagsOnResource,
    tagsOnResource,
    setTagsOnResource,
  } = useCreateTagsOnResource(ResourceType.Prompt);

  const { trigger: createRepo, isMutating: createRepoLoading } =
    useCreateRepoMutation({
      onError: (error) => {
        toast.createToast({
          title: 'Failed to create prompt',
          description: error.message,
        });
      },
    });

  const { data: webhooks } = useFetchWorkspacePromptWebhook({});

  const [excludedWebhookIds, setExcludedWebhookIds] = useState<string[]>([]);

  const onSubmit = async () => {
    try {
      const res = await createRepo({
        json: repo,
      });
      if (res?.repo !== undefined) {
        if (
          tagsOnResource &&
          tagsOnResource.length > 0 &&
          !res.repo.is_public
        ) {
          await createTagsOnResource(res.repo.id);
        }
        let commitRes;
        if (manifest) {
          commitRes = await createCommit({
            url: `${hubApiCommitsPath}/${
              tenantHandle ? res.repo.full_name : `-/${res.repo.repo_handle}`
            }`,
            json: {
              parent_commit: res.repo.last_commit_hash,
              manifest: manifest,
              skip_webhooks: excludedWebhookIds,
            },
          });
        }
        if (commitRes) {
          onSuccess?.(
            {
              ...res.repo,
              latest_commit_manifest: commitRes.commit.manifest,
              last_commit_hash: commitRes.commit.commit_hash,
            },
            commitRes.commit
          );
          onClose();
          toast.createToast({
            title: 'Prompt created',
            description: (
              <Link
                to={`/${appPromptsIndexPath}/${res.repo.repo_handle}/${commitRes?.commit.commit_hash}?organizationId=${res.repo.tenant_id}`}
                className="underline"
              >
                View in the prompt hub
              </Link>
            ),
          });
        }
      }
    } catch (error: any) {
      toast.createToast({
        title: 'Failed to create prompt',
        description: error.message ?? 'Something went wrong',
        error: true,
      });
    }
  };

  const getError = () => {
    if (hasPromptWithSameName) {
      return new Error('A prompt with this name already exists');
    }
    return getRepoHandleValidationError(repo.repo_handle);
  };

  return (
    <RepoForm
      repo={repo}
      setRepo={setRepo}
      tenantHandle={tenantHandle}
      onSubmit={onSubmit}
      onCancel={onClose}
      isLoading={isCommitMutating || createRepoLoading}
      isValidatingPrompts={isValidatingPrompts}
      error={getError()}
      mode="create"
      hideTitle={true}
      minimal={true}
      tagsOnResource={tagsOnResource}
      setTagsOnResource={setTagsOnResource}
      webhooks={webhooks ?? []}
      excludedWebhookIds={excludedWebhookIds}
      setExcludedWebhookIds={setExcludedWebhookIds}
    />
  );
};

export const PlaygroundCreatePromptModal = ({
  open,
  manifest,
  onClose,
  onSuccess,
}: {
  open: boolean;
  manifest?: SerializedConstructor;
  onClose: () => void;
  onSuccess?: (
    repoWithLookups: RepoWithLookupsSchema,
    commit: CommitWithLookupsSchema
  ) => void;
}) => {
  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog variant="outlined" sx={{ maxWidth: '450px' }}>
        <ModalClose size="lg" />
        <PromptNewForm
          manifest={manifest}
          onClose={onClose}
          onSuccess={onSuccess}
        />
      </ModalDialog>
    </Modal>
  );
};
